import React, {useId, useMemo} from 'react';

import FieldDescription from '@pb/kit-component-field-description';
import {useListbox} from '@pb/kit-component-listbox';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';

import {ComboBoxInput} from './combo-box-input.component';
import {DefaultItem} from './combo-box-item-default.component';
import {ComboboxListbox} from './combo-box-listbox.component';
import {useComboboxValue} from './use-combobox-value';
import {iterateItems} from './utils/iterate-items';


import type {ComboBoxInputProps} from './combo-box-input.component';
import type {ComboboxItemProps} from './combo-box-item.component';
import type {ComboboxListboxProps} from './combo-box-listbox.component';
import type {UseListboxProps} from '@pb/kit-component-listbox';
import type {ReactElement, ReactNode} from 'react';


type _ComboBoxInputProps = Omit<ComboBoxInputProps, 'selectedItemsProps' | 'isDefaultItemSelected' | 'onChange' | 'onToggle'>;

export interface ComboBoxProps<Value> extends _ComboBoxInputProps {
    /**
     * If menu is shown
     * */
    shown?: boolean,

    /**
     * Setter for state if menu is shown
     * */
    setShown?: (shown: boolean) => void,

    /**
     * Selected Item value or array of values. If array - behaves as multiselectable
     */
    value?: Value,

    /**
     * Helper text
     * */
    helperText?: ReactNode,

    /**
     * Component's input name identifier
     */
    name?: string,
    /**
     * Callback on change value, if multiselectable is true, then receives array of values
     */
    onChange?: (value: Value, name: string) => void,

    /**
     * ComboBox menu alignment direction
     */
    direction?: 'left' | 'right',

    /**
     * Amount of items for scroll
     */
    itemsForScroll?: number,

    /**
     * Callback on change shown
     */
    onToggle?: (shown: boolean) => void,
    /**
     * If true - Check icon on selected item will be hidden
     * @deprecated It will be removed in 6 months 09-15-2025. use Item children instead
     * */
    dropdown?: boolean,
    /**
     * List of Items/Groups.
     * It accepts Items, Groups, DefaultItem as children. Combobox connects to them with `Children.map`. You should pass them as direct children of Combobox, with no wrappers in between. It can render any children, e.g. loader, inside the listbox, but it will not interact with the Combobox as an Item.
     * */
    children?: ReactNode,

    type?: UseListboxProps['type'],

    allowedPlacements?: UseListboxProps['allowedPlacements'],
    portalProps?: ComboboxListboxProps<unknown>['portalProps'],
}

/**
 * ComboBox component
 *
 * Use combo-box when the user needs to select one or more option from the list.
 *
 * It accepts Items, Groups, DefaultItem as children. Combobox connects to them with `Children.map`. You should pass them as direct children of Combobox, with no wrappers in between. It can render any children, e.g. loader, inside the listbox, but it will not interact with the Combobox as an Item.
 *
 * #### A11y usage notes:
 *
 * Combobox implements https://www.w3.org/WAI/ARIA/apg/patterns/combobox/. It should have an associated label or have  aria-label or aria-labelledby attributes.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-combo-box}
 */
export function ComboBox<ItemValue, Value extends ItemValue | ItemValue[]>(
    props: ComboBoxProps<Value>,
) {
    const {
        shown: shownProp,
        setShown: setShownProp,
        helperText,
        name,
        disabled = false,
        placeholder,
        color,
        direction,
        itemsForScroll,
        value: valueProp,
        multiselectable = Array.isArray(valueProp),
        onChange: onChangeProp,
        onToggle,
        dropdown,
        children,
        type,
        allowedPlacements,
        portalProps,
        ...inputProps
    } = props;

    const [shown, setShown] = useControlledState(shownProp, setShownProp, false);

    const itemsProps = useMemo(() => {
        const newProps: ComboboxItemProps<ItemValue>[] = [];
        iterateItems<ItemValue>(children, item => {
            newProps.push(item.props);
        });
        return newProps;
    }, [children]);

    const {
        value,
        onChange,
    } = useComboboxValue<ItemValue, Value>({
        multiselectable,
        value: valueProp,
        onChange: onChangeProp,
        name,
        setShown,
    });

    const defaultItemValue = useMemo(() => {
        let defaultItem: ReactElement<ComboboxItemProps<ItemValue>> | undefined;
        iterateItems<ItemValue>(children, item => {
            if (item.type === DefaultItem) {
                defaultItem = item;
            }
        });
        return defaultItem?.props.value;
    }, [children]);

    const isDefaultItemSelected = value.length === 1 && value[0] === defaultItemValue;

    let selectedIndex = value.length
        ? itemsProps.findIndex(item => item.value === value[0])
        : null;

    if (selectedIndex === -1) {
        selectedIndex = null;
    }

    const {
        getAnchorProps,
        anchorRef,
        getListBoxProps,
    } = useListbox({
        shown,
        setShown,
        direction,
        itemsForScroll,
        typeaheadEnabled: true,
        onToggle,
        type,
        allowedPlacements,
        listNavigationProps: {
            selectedIndex,
        },
    });

    const descriptionId = useId();

    const onKeyDownHandler = () => {
        if (!shown) {
            setShown(true);
            return;
        }

        const {activeIndex} = getListBoxProps();
        if (typeof activeIndex !== 'number') {
            return;
        }
        onChange(itemsProps[activeIndex]);
    };

    const selectedItemsProps = useMemo(() => {
        const itemsPropsByValue = new Map<ItemValue, ComboboxItemProps<ItemValue>>();
        iterateItems<ItemValue>(children, item => {
            itemsPropsByValue.set(item.props.value, item.props);
        });

        return (
            value
                .map(itemValue => itemsPropsByValue.get(itemValue))
                .filter(Boolean) as ComboboxItemProps<ItemValue>[]
        );
    }, [value, children]);

    return (
        <>
            <ComboBoxInput
                aria-label={(placeholder || name) ?? ''}
                aria-describedby={helperText ? descriptionId : undefined}
                {...{
                    disabled,
                    placeholder,
                    color,
                    multiselectable,
                    isDefaultItemSelected,
                    ...getAnchorProps(inputProps),
                    selectedItemsProps,
                    onKeyDownHandler,
                }}
                ref={anchorRef}
            />
            <ComboboxListbox
                aria-label={(placeholder || name) ?? ''}
                {...{
                    isDefaultItemSelected,
                    multiselectable,
                    dropdown,
                    value,
                    portalProps,
                    ...getListBoxProps(),
                    onChange,
                }}>
                {children}
            </ComboboxListbox>
            {helperText ? (
                <FieldDescription id={descriptionId} color={color === 'ERROR' ? 'Types.ERROR' : undefined}>
                    {helperText}
                </FieldDescription>
            ) : null}
        </>
    );
}
